import { pageWhere } from '@/lib/experience-labels';
import type { VariableStanding, ZonePlace } from '@/lib/variable-pill';
import { pillOf } from '@/lib/variable-pill';
import type { ExperienceVariableKind } from '@matter_extractor/shared';
import { ANALYST_READING_ORPHAN_LABEL, assertNever } from '@matter_extractor/shared';

/**
 * Ce que la fiche d'une pastille DIT, par nature (refonte modulaire §8.6 ;
 * grill Variables du 2026-09-16, D6 ; maquette `variables.html`, planche 3 ;
 * issue #88).
 *
 * « Au survol ou au focus clavier, une fiche dit sa nature, sa provenance (la
 * page et le bloc, ou l'étape et l'obligation), ce qu'elle rend À CET ENDROIT -
 * matière pour l'interviewer, extraits pour la personne - et offre un lien
 * "Ouvrir". » The sentences are here, pure, because the same fiche opens from
 * five zones and a wording assembled in a render is a wording nothing asserts.
 *
 * ## « Rend ici » depends on WHERE the pill is
 *
 * D4: an objective cited on a page shown to the person renders her own
 * extracts, one per line, without name or status - while in the Contexte, in
 * the overlay and in a protocol's direction it renders the interviewer's full
 * matter. One variable, two renderings, and the fiche says which one this pill
 * will produce rather than describing the variable in the abstract.
 *
 * ## A criterion has no content here
 *
 * Its fiche is the one the protocol editor already opens on the line of an
 * objective (`criterion-card-popover.tsx`), reused as is (D6): it reads the
 * criterion from the base, which a pure module cannot. `ficheContentOf` answers
 * null for it and the component mounts the popover instead.
 *
 * ## The two zones of a protocol's own editor (issue #158)
 *
 * A DIRECTION (`place` null) serves the interviewer the matter, as it always
 * did. The READING for the analyst (`READING_PLACE`) serves the NAME of the
 * objective, « tel qu'il est dans la version jouée » (noyau.md §4.9). And an
 * ORPHAN - a token whose objective the version no longer carries - promises no
 * refusal of « Enregistrer une nouvelle version » in either, because the
 * protocol never refuses it (V2, V5): in the reading the analyst reads
 * « (objectif retiré) », in a direction « Passer en production » of the
 * experiences that play the protocol is refused. Both say it in the YELLOW of
 * the protocol's boxes; the red stays the experience's, whose save refuses.
 *
 * An UNKNOWN criterion in a direction is the same régime with another door
 * (review of !478): `protocolProblems` never reads a direction's tokens, so
 * the version saves, and it is « Passer en production » of THIS protocol that
 * `productionBlockers` refuses (`criterion-unknown`) - yellow too.
 */

/** Where « Ouvrir » leads, or null when there is nowhere to go from here. */
export type FicheLink =
  | { readonly kind: 'page'; readonly pageId: string; readonly label: string }
  | { readonly kind: 'protocol'; readonly protocolId: string; readonly label: string };

/** One of the small pills under the head of the fiche. */
export type FichePill = {
  readonly label: string;
  readonly tone: 'objective' | 'production' | 'development' | 'neutral';
};

export type FicheContent = {
  /** « Champ · Input, une ligne, obligatoire » - the nature, beside the pill. */
  readonly kind: string;
  /** « Saisi » → « page 1, Consentement et identité » - the provenance rows. */
  readonly rows: readonly { readonly term: string; readonly detail: string }[];
  readonly pills: readonly FichePill[];
  /** The direction of an objective, quoted, or null. */
  readonly direction: string | null;
  /** « Rend ici : … » - what this pill will produce where it stands, or null. */
  readonly renders: string | null;
  /** The yellow box of a variable out of reach, or null. */
  readonly warning: string | null;
  /** The red box of a variable nothing resolves, or null. */
  readonly refusal: string | null;
  readonly link: FicheLink | null;
};

const KIND_NOUN: Readonly<Record<ExperienceVariableKind, string>> = {
  field: 'Champ',
  choice: 'Choix',
  objective: 'Objectif',
  criterion: 'Critère',
  noyau: 'Noyau retenu',
};

/**
 * « page 1 » - where the pill stands, in words. The page's NAME is not in the
 * standing - the zone knows its index, the fiche opens from inside that very
 * page - so the row names the rank alone, which is also what the save box's
 * line says first.
 */
function placeWhere(place: ZonePlace): string {
  switch (place.kind) {
    case 'page':
      return pageWhere(place.pageIndex, null);
    case 'context':
      return 'le Contexte de la page protocole';
    case 'overlay':
      return 'la surcouche du persona';
    case 'reading':
      return 'la lecture pour l’analyste';
    case 'rangement':
      return 'la consigne du rangeur';
    default:
      return assertNever(place, 'placeWhere');
  }
}

/** « Rend ici » of an objective in the reading for the analyst (§4.9, the maquette word for word). */
export const READING_RENDERS =
  'Rend ici, servi à l’analyste : le nom de l’objectif, tel qu’il est dans la version jouée.';

/** « Rend ici » of an objective in the consigne of the rangeur (#177): the name, as for the analyst. */
export const RANGEMENT_RENDERS =
  'Rend ici, servi au rangeur : le nom de l’objectif, tel qu’il est dans la version jouée.';

/**
 * What a token that resolves to nothing says in a zone of the PROTOCOL's own
 * editor (issue #158; see the head of this file), or null where the refusal of
 * an experience's zone holds - `refusalOf`.
 *
 * The reading cites objectives only (N48): any other token there names
 * nothing, and goes to the analyst as typed. A direction cites objectives and
 * criteria, and says nothing of its own for the other kinds.
 */
function protocolUnresolved(
  kind: ExperienceVariableKind,
  place: ZonePlace | null,
): { readonly kind: string; readonly warning: string } | null {
  const ranger = place !== null && place.kind === 'rangement';
  const reading = place !== null && (place.kind === 'reading' || ranger);
  if (place !== null && !reading) return null;
  if (kind === 'objective') {
    return {
      kind: `${KIND_NOUN.objective} · identifiant sans réponse`,
      warning: reading
        ? `Ne résout plus : cet objectif n’existe pas dans cette version du protocole. ${ranger ? 'Le rangeur lira' : 'L’analyste lira'} « ${ANALYST_READING_ORPHAN_LABEL} » à sa place ; la version s’enregistre quand même.`
        : 'Ne résout plus : cet objectif n’existe pas dans cette version du protocole. La version s’enregistre, mais « Passer en production » des expériences qui jouent ce protocole sera refusé tant qu’elle y est.',
    };
  }
  if (!reading) {
    return kind === 'criterion'
      ? {
          kind: `${KIND_NOUN.criterion} · identifiant sans réponse`,
          warning:
            'Ne résout plus : ce critère n’est plus dans la bibliothèque. La version s’enregistre, mais « Passer en production » de ce protocole sera refusé tant qu’il y est cité.',
        }
      : null;
  }
  return ranger
    ? {
        kind: `${KIND_NOUN[kind]} · ne se cite pas dans la consigne`,
        warning: 'La consigne ne cite que des objectifs : le rangeur lira ce jeton tel quel.',
      }
    : {
        kind: `${KIND_NOUN[kind]} · ne se cite pas dans la lecture`,
        warning: 'La lecture ne cite que des objectifs : l’analyste lira ce jeton tel quel.',
      };
}

/** « Ne résout plus : … » - why a token names nothing, per kind. */
function refusalOf(standing: VariableStanding): string {
  const tail = ' « Enregistrer une nouvelle version » sera refusé tant qu’elle est citée.';
  switch (standing.variable.kind) {
    case 'field':
      return `Ne résout plus : aucun input ni checkbox de la timeline ne porte cet identifiant. Le bloc a peut-être été supprimé.${tail}`;
    case 'choice':
      return `Ne résout plus : aucune page de la timeline ne retient un choix sous cet identifiant. La page a peut-être été supprimée, ou ses boutons ne retiennent plus le choix.${tail}`;
    case 'objective':
      return `Ne résout plus : cet objectif n’existe pas dans le protocole choisi. Il a peut-être été retiré d’une version à l’autre.${tail}`;
    case 'criterion':
      return `Ne résout plus : ce critère n’est plus dans la bibliothèque.${tail}`;
    case 'noyau':
      // With a first choice, `@noyau` has facts and never reaches this line:
      // it only names nothing while no block gives it a value (noyau.md §3.4).
      return `Ne résout pas : aucun bloc Noyau en premier choix ne donne encore le noyau retenu.${tail}`;
    default:
      return assertNever(standing.variable.kind, 'refusalOf');
  }
}

/** « Hors de portée : … » - why a variable that exists renders nothing HERE (D3). */
function warningOf(standing: VariableStanding, place: ZonePlace | null): string | null {
  const breach = standing.breach;
  if (breach === null || place === null) return null;
  const here = place.kind === 'page' ? `la page ${place.pageIndex + 1}` : placeWhere(place);
  const tail = ' « Enregistrer une nouvelle version » sera refusé tant que l’ordre ne change pas.';
  // `@noyau` is not TYPED on a page: the first choice GIVES it (noyau.md §3.3).
  if (standing.variable.kind === 'noyau') {
    return `Hors de portée : le noyau retenu n’existe qu’à partir de la page qui suit le premier choix, et ${here} n’en est pas une.${tail}`;
  }
  switch (breach.reason) {
    case 'same-page':
      return `Hors de portée : ${here} est celle qui saisit cette valeur, et une valeur n’existe pas tant que sa page s’affiche.${tail}`;
    case 'before-declaration':
      return `Hors de portée : ${here} s’affiche avant la saisie, la personne verrait le jeton brut.${tail}`;
    case 'before-protocol':
      return `Hors de portée : ${here} s’affiche avant la page protocole, et un objectif n’a encore aucune matière.${tail}`;
    default:
      return assertNever(breach.reason, 'warningOf');
  }
}

/** « Ouvrir la page 4 → » - the page that declares a field or a choice. */
function pageLink(pageId: string, pageIndex: number): FicheLink {
  return { kind: 'page', pageId, label: `Ouvrir la page ${pageIndex + 1} →` };
}

/**
 * The fiche of a pill standing at `place`, or null for a criterion - whose
 * fiche is the popover of the protocol editor, mounted by the component.
 */
export function ficheContentOf(
  standing: VariableStanding,
  place: ZonePlace | null,
): FicheContent | null {
  const { variable, facts } = standing;
  const noun = KIND_NOUN[variable.kind];

  if (standing.pending) {
    return {
      kind: `${noun} · lecture en cours`,
      rows: [],
      pills: [],
      direction: null,
      renders: null,
      warning: null,
      refusal: null,
      link: null,
    };
  }

  if (facts === null) {
    const unresolved = protocolUnresolved(variable.kind, place);
    return {
      kind: unresolved?.kind ?? `${noun} · identifiant sans réponse`,
      rows: [],
      pills: [],
      direction: null,
      renders: null,
      warning: unresolved?.warning ?? null,
      refusal: unresolved === null ? refusalOf(standing) : null,
      link: null,
    };
  }

  const warning = warningOf(standing, place);
  const cited =
    place === null || standing.breach === null ? [] : [{ term: 'Cité', detail: placeWhere(place) }];

  switch (facts.kind) {
    case 'field': {
      const where = pageWhere(facts.pageIndex, facts.pageName);
      if (facts.blockType === 'checkbox') {
        return {
          kind: `Champ · Checkbox, ${facts.required ? 'obligatoire pour continuer' : 'facultative'}`,
          rows: [
            { term: 'Cochée', detail: where },
            { term: 'Libellé du bloc', detail: `« ${facts.label} »` },
            ...cited,
          ],
          pills: [],
          direction: null,
          renders:
            warning === null ? 'Rend ici : oui ou non, parce qu’une phrase se complète.' : null,
          warning,
          refusal: null,
          link: pageLink(facts.pageId, facts.pageIndex),
        };
      }
      const lines = facts.lines === 'multi' ? 'multi-lignes' : 'une ligne';
      const rows = [
        { term: 'Saisi', detail: where },
        { term: 'Libellé du bloc', detail: `« ${facts.label} »` },
        ...(facts.format === null
          ? []
          : [{ term: 'Format', detail: facts.format === 'email' ? 'email' : 'texte' }]),
        ...cited,
      ];
      return {
        kind: `Champ · Input, ${lines}, ${facts.required ? 'obligatoire' : 'facultatif'}`,
        rows,
        pills: [],
        direction: null,
        renders:
          warning === null
            ? 'Rend ici : la valeur saisie par la personne, telle qu’elle l’a tapée.'
            : null,
        warning,
        refusal: null,
        link: pageLink(facts.pageId, facts.pageIndex),
      };
    }
    case 'choice':
      return {
        kind: `Choix · une page à ${facts.buttons.length} boutons`,
        rows: [
          { term: 'Choisi', detail: pageWhere(facts.pageIndex, facts.pageName) },
          { term: 'Boutons', detail: facts.buttons.join(' · ') },
          ...cited,
        ],
        pills: [],
        direction: null,
        renders:
          warning === null
            ? 'Rend ici : le libellé du bouton pressé. Le choix appartient à la page, pas au bouton (§8.6).'
            : null,
        warning,
        refusal: null,
        link: pageLink(facts.pageId, facts.pageIndex),
      };
    case 'objective': {
      const protocol = facts.protocol;
      const pills: FichePill[] = [
        {
          label: facts.mandatory ? 'obligatoire' : 'optionnel',
          tone: facts.mandatory ? 'objective' : 'neutral',
        },
      ];
      if (protocol !== null) {
        // « Entretien fondateur · latest v5 » when the page follows the latest dev
        // version, « Entretien fondateur · v3 » when it pins one.
        pills.push({
          label: `${protocol.name} · ${
            protocol.pin === 'latest'
              ? `latest${protocol.version === null ? '' : ` v${protocol.version}`}`
              : `v${protocol.pin}`
          }`,
          tone: protocol.pin === 'latest' ? 'development' : 'neutral',
        });
      }
      const forPerson = place !== null && place.kind === 'page';
      const forAnalyst = place !== null && place.kind === 'reading';
      const forRanger = place !== null && place.kind === 'rangement';
      return {
        kind: `Objectif · étape ${facts.stepIndex + 1}, ${facts.stepName}`,
        rows: cited,
        pills,
        direction: facts.direction === '' ? null : facts.direction,
        renders:
          warning !== null
            ? null
            : forAnalyst
              ? READING_RENDERS
              : forRanger
                ? RANGEMENT_RENDERS
                : forPerson
                  ? 'Rend ici, montré à la personne : ses extraits retenus, dans ses mots, un par ligne. Ni nom, ni statut ; rien si aucun extrait n’a été retenu.'
                  : 'Rend ici, servi à l’interviewer : la matière de l’objectif, son nom, son statut et ses extraits, ou « rien encore ».',
        warning,
        refusal: null,
        link:
          protocol === null
            ? null
            : { kind: 'protocol', protocolId: protocol.id, label: 'Ouvrir le protocole ↗' },
      };
    }
    case 'criterion':
      return null;
    case 'noyau':
      // « Sa fiche dit sa nature (« le noyau retenu »), sa provenance (la page
      // du premier choix) et ce qu'elle rend » (noyau.md §3.3). « Au premier
      // choix » and no longer « la carte prise » since #169: beyond the gap
      // threshold the principal is retained, whatever the card (§3.8).
      return {
        kind: 'Noyau retenu · au premier choix',
        rows: [
          { term: 'Premier choix', detail: pageWhere(facts.pageIndex, facts.pageName) },
          ...cited,
        ],
        pills: [],
        direction: null,
        renders:
          warning === null
            ? 'Rend ici : le nom du noyau retenu, tel qu’il est déposé - « Souverain Magicien ». Rien si la séquence a été sautée.'
            : null,
        warning,
        refusal: null,
        link: pageLink(facts.pageId, facts.pageIndex),
      };
    default:
      return assertNever(facts, 'ficheContentOf');
  }
}

/** The accessible name of the fiche: « Fiche de la variable @prénom ». */
export function ficheName(standing: VariableStanding): string {
  return `Fiche de la variable ${pillOf(standing).label}`;
}
